import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

export const useSubscriptionTiers = () => {
  const [tiers, setTiers] = useState<string[]>([]);
  const [tierLabels, setTierLabels] = useState<Record<string, string>>({});
  const [tierDescriptions, setTierDescriptions] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getSubscriptionTiers();
      setTiers(data.tiers || []);
      setTierLabels(data.tier_labels || {});
      setTierDescriptions(data.tier_descriptions || {});
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch subscription tiers');
      console.error('Error fetching subscription tiers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  return {
    tiers,
    tierLabels,
    tierDescriptions,
    loading,
    error,
    refetch: fetchTiers
  };
};

export const useSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getSubscriptions();
      setSubscriptions(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch subscription data');
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscription = async (userId: number, newTier: string, reason?: string) => {
    try {
      await adminAPI.updateUserSubscription(userId, newTier, reason);
      await fetchSubscriptions(); // Refresh data
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update subscription');
      throw err;
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return {
    subscriptions,
    loading,
    error,
    refetch: fetchSubscriptions,
    updateUserSubscription
  };
};

export const useBilling = () => {
  const [billing, setBilling] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminAPI.getBillingOverview();
      setBilling(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch billing data');
      console.error('Error fetching billing:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
  }, []);

  return {
    billing,
    loading,
    error,
    refetch: fetchBilling
  };
};
